/**
 * CR107: Magical UX Layer - Celebration Effects
 * 
 * Provides confetti and sparkle animations for magical first build moments
 */

import { useEffect, useState } from "react";
import { useMagicalUX } from "./MagicalUXProvider";

interface Confetti {
  id: string;
  x: number;
  y: number;
  vx: number;
  vy: number;
  color: string;
  rotation: number;
  rotationSpeed: number;
  size: number;
}

const CELEBRATION_COLORS = [
  'hsl(var(--primary))',
  'hsl(var(--secondary))',
  'hsl(142 76% 36%)', // green
  'hsl(221 83% 53%)', // blue
  'hsl(262 83% 58%)', // purple
  'hsl(346 87% 43%)', // red
];

export function CelebrationEffects() {
  const { state } = useMagicalUX();
  const [confetti, setConfetti] = useState<Confetti[]>([]);
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    if (state.hasSeenFirstBuild && state.lastAction === 'first-build') {
      triggerCelebration();
    }
  }, [state.hasSeenFirstBuild, state.lastAction]);

  const triggerCelebration = () => {
    setIsActive(true);
    
    // Create confetti burst
    const newConfetti: Confetti[] = [];
    for (let i = 0; i < 50; i++) {
      newConfetti.push({
        id: `confetti-${i}`,
        x: window.innerWidth / 2 + (Math.random() - 0.5) * 200,
        y: window.innerHeight / 2 + (Math.random() - 0.5) * 100,
        vx: (Math.random() - 0.5) * 8,
        vy: Math.random() * -8 - 2,
        color: CELEBRATION_COLORS[Math.floor(Math.random() * CELEBRATION_COLORS.length)],
        rotation: Math.random() * 360,
        rotationSpeed: (Math.random() - 0.5) * 10,
        size: 4 + Math.random() * 6,
      });
    }
    
    setConfetti(newConfetti);

    // Animate confetti
    let animationFrame: number;
    const animate = () => {
      setConfetti(prev => 
        prev.map(particle => ({
          ...particle,
          x: particle.x + particle.vx,
          y: particle.y + particle.vy,
          vy: particle.vy + 0.3, // gravity
          rotation: particle.rotation + particle.rotationSpeed,
        })).filter(particle => 
          particle.y < window.innerHeight + 100 && 
          particle.x > -100 && 
          particle.x < window.innerWidth + 100
        )
      );
      
      animationFrame = requestAnimationFrame(animate);
    };
    
    animationFrame = requestAnimationFrame(animate);

    // Clean up after 5 seconds
    setTimeout(() => {
      setIsActive(false);
      setConfetti([]);
      cancelAnimationFrame(animationFrame);
    }, 5000);
  };

  if (!isActive || confetti.length === 0) return null;

  return (
    <div className="fixed inset-0 pointer-events-none z-[9998]">
      {confetti.map(particle => (
        <div
          key={particle.id}
          className="absolute rounded-sm"
          style={{
            left: particle.x,
            top: particle.y,
            width: particle.size,
            height: particle.size,
            backgroundColor: particle.color,
            transform: `rotate(${particle.rotation}deg)`,
            opacity: 0.8,
          }}
        />
      ))}
    </div>
  );
}